<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"
import useAttributes from "../../use/useAttributes"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const attributes = computed(() => store.state.attributes)
const categoriesWithAttributes = computed(
  () => store.state.categoriesWithAttributes
)

const {
  search,
  categoryId,
  perPage,
  sortField,
  sortDirection,
  getAttributes,
  sortAttributes,
} = useAttributes()
const { handleInput } = useDebouncedFunction(getAttributes)

const emit = defineEmits(["clickEdit"])

onMounted(() => {
  getAttributes()
  store.dispatch("getCategoriesWithAttributes")
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getAttributes(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editAttribute = (cat) => {
  emit("clickEdit", cat)
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی نام دسته بندی..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getAttributes(null)"
              v-model="categoryId"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="">انتخاب دسته بندی</option>
              <option
                v-for="category in categoriesWithAttributes.data"
                :value="category.id"
                :key="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getAttributes(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="attributeTable">
          <thead class="table-light text-muted">
            <tr>
              <TableHeaderCell
                field="attribute_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortAttributes('attribute_name')"
              >
                نام ویژگی
              </TableHeaderCell>
              <TableHeaderCell
                field="attribute_displayname"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortAttributes('attribute_displayname')"
              >
                نام نمایشی ویژگی
              </TableHeaderCell>
              <TableHeaderCell
                field="category_id"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortAttributes('category_id')"
              >
                دسته بندی
              </TableHeaderCell>
              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortAttributes('created_at')"
              >
                تاریخ ایجاد
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody
            class="list"
            v-if="attributes.loading || !attributes.data.length"
          >
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="attributes.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ ویژگی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(attribute, index) of attributes.data" :key="index">
              <td class="attribute_name">{{ attribute.name }}</td>
              <td class="attribute_displayname">
                {{ attribute.display_name }}
              </td>
              <td class="category_name">{{ attribute.category_name }}</td>
              <td class="date">{{ toPersianDateTime(attribute.created_at) }}</td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editAttribute(attribute)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="attributes.total > attributes.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of attributes.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
